// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Bygning shell's selection: which mode, which type/part/sample/room is
 * in focus, and how the view is set up. Everything except the multi-part
 * selection lives in the URL (PLAN.md "Layout contract"), so a reload, a
 * shared link and Back all restore it:
 *
 *   ?type=&part=&sample=&room=&storey=&lens=&view=&tview=&fields=&q=&env=&star=&errors=&page=&rapport=&ev=&edit=
 *
 * (`view` is Ressourcer's Model / Tabel / Galleri; `q`/`env`/`star`/`errors`
 * are its filters, shared by all three views; `fields` is the template — or
 * `all`, Alle felter — the type inspector and Tabel › Typer show,
 * `fieldView.ts`; `page` and `rapport=miljoe`
 * pick the leaf of Rapporter & indberetning's tree, `rapportTree.ts`;
 * `edit=sag` is the Sagsdata editor, a dialog over Overblik.)
 *
 * Keys this module does not own — the Ground's `?pano=` and `?captureFor=`,
 * Miljø's `?ny=` — are kept untouched by `format`. Pure: no React, no DOM.
 */

import type { EnvFilter } from '../kortlaegning/model';
import { formatFieldView, parseFieldView, type FieldView } from './fieldView';
import type { ModeId } from './modes';
import type { ReportId } from './rapportTree';
import { storeyRooms, type Storey } from './storeys';

export type Lens = 'mat' | 'hier' | 'rev';
export type ViewKind = 'model' | 'tabel' | 'galleri';
export type TableView = 'typer' | 'dele';
export type EvidenceKind = 'foto' | 'pano' | 'seg';
export type EditKind = 'sag';

export interface Selection {
  mode: ModeId;
  /** `?type=`: the survey type in focus. */
  typeId: number | null;
  /** `?part=`: the building part in focus (a part code). */
  partCode: string | null;
  /** `?sample=`: the sample in focus (Miljø). */
  sampleId: number | null;
  /** `?room=`: a room focus (room id); null = hele bygningen. */
  room: number | null;
  /**
   * `?storey=`: a storey focus (`Storey.id`: the stored storey's idx, or the
   * heuristic's index when none is stored, gap 7); null = hele bygningen. A room focus wins over it in the Ground.
   */
  storey: number | null;
  /** `?lens=`: colour-by in Ressourcer; default `mat`. */
  lens: Lens;
  /** `?view=`: Model, Tabel or Galleri in Ressourcer; default `model`. */
  view: ViewKind;
  /** `?tview=`: which table in Tabel; default `typer`. */
  tview: TableView;
  /**
   * `?fields=`: the fields the type inspector and Tabel › Typer show (a
   * template id, or `all`); null (absent) = the project's remembered choice.
   */
  fieldView: FieldView | null;
  /** `?q=`:Ressourcer's search (type, EAK, part code, room name); '' = none. */
  search: string;
  /** `?env=`: Ressourcer's Miljøstatus filter; null = alle. */
  env: EnvFilter | null;
  /** `?star=1`: only ★ types and ★ parts (Vigtige). */
  starred: boolean;
  /** `?errors=1`: show rejected types and dismissed parts too (Fejldetektion). */
  errors: boolean;
  /** `?page=`: Rapport's page index (0-based); `INDBERETNING_PAGE` for Indberetning. */
  page: number;
  /** `?rapport=miljoe`: the Miljøkortlægning's leaves; null (absent) = Ressourcekortlægning. */
  rapport: ReportId | null;
  /** `?ev=`: the evidence tile (centre) for the part in focus, or null. */
  ev: EvidenceKind | null;
  /** `?edit=sag`: the Sagsdata editor (a dialog over Overblik) is open; null when closed. */
  edit: EditKind | null;
  /** `?el=`: Rediger › Geometri's selected element (a wall, opening, space or slab guid); null = none. */
  element: string | null;
  /** `?usikre=1`: Rediger › Geometri's tree shows only the uncertain elements. */
  uncertain: boolean;
  /** Parts picked for a bulk action (PartBar). Not in the URL: transient. */
  selParts: string[];
}

/** `?page=indberetning` — the Rapport mode's Indberetning section (old `/indberetning`). */
export const INDBERETNING_PAGE = -1;

/** The query keys `parse`/`format` own; every other key is passed through. */
export const SELECTION_KEYS = [
  'type', 'part', 'sample', 'room', 'storey', 'lens', 'view', 'tview', 'fields', 'q', 'env', 'star', 'errors', 'page', 'rapport', 'ev', 'edit', 'el', 'usikre',
] as const;

/** The patch that clears every Ressourcer filter, room included ("Nulstil filtre"). */
export const NO_FILTER_PATCH: Partial<Selection> = { room: null, search: '', env: null, starred: false, errors: false };

const LENSES: readonly Lens[] = ['mat', 'hier', 'rev'];
const VIEWS: readonly ViewKind[] = ['model', 'tabel', 'galleri'];
const TVIEWS: readonly TableView[] = ['typer', 'dele'];
const ENVS: readonly EnvFilter[] = ['ren', 'afventer', 'forurenet'];
const EVIDENCE: readonly EvidenceKind[] = ['foto', 'pano', 'seg'];
const EDITS: readonly EditKind[] = ['sag'];
/** `?rapport=` values; `ressource` is the default and never written. */
const REPORTS: readonly ReportId[] = ['ressource', 'miljoe'];

export function defaultSelection(mode: ModeId): Selection {
  return {
    mode,
    typeId: null,
    partCode: null,
    sampleId: null,
    room: null,
    storey: null,
    lens: 'mat',
    view: 'model',
    tview: 'typer',
    fieldView: null,
    search: '',
    env: null,
    starred: false,
    errors: false,
    page: 0,
    rapport: null,
    ev: null,
    edit: null,
    element: null,
    uncertain: false,
    selParts: [],
  };
}

function positiveInt(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return n > 0 && Number.isSafeInteger(n) ? n : null;
}

function nonNegativeInt(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const n = Number(value);
  return Number.isSafeInteger(n) ? n : null;
}

function oneOf<T extends string>(list: readonly T[], value: string | null, fallback: T): T {
  return value !== null && (list as readonly string[]).includes(value) ? (value as T) : fallback;
}

/**
 * The selection a query string describes, for `mode`. Anything malformed
 * falls back to its default; a part code is any non-empty string.
 */
export function parse(search: string | URLSearchParams, mode: ModeId): Selection {
  const q = typeof search === 'string' ? new URLSearchParams(search) : search;
  const base = defaultSelection(mode);
  const part = q.get('part');
  const page = q.get('page');
  const ev = q.get('ev');
  const edit = q.get('edit');
  const env = q.get('env');
  const el = q.get('el');
  return {
    ...base,
    typeId: positiveInt(q.get('type')),
    partCode: part !== null && part.trim() !== '' ? part : null,
    sampleId: positiveInt(q.get('sample')),
    room: nonNegativeInt(q.get('room')),
    storey: nonNegativeInt(q.get('storey')),
    lens: oneOf(LENSES, q.get('lens'), base.lens),
    view: oneOf(VIEWS, q.get('view'), base.view),
    tview: oneOf(TVIEWS, q.get('tview'), base.tview),
    fieldView: parseFieldView(q.get('fields')),
    search: q.get('q') ?? base.search,
    env: env !== null && (ENVS as readonly string[]).includes(env) ? (env as EnvFilter) : null,
    starred: q.get('star') === '1',
    errors: q.get('errors') === '1',
    page: page === 'indberetning' ? INDBERETNING_PAGE : (nonNegativeInt(page) ?? base.page),
    rapport: oneOf(REPORTS, q.get('rapport'), 'ressource') === 'miljoe' ? 'miljoe' : null,
    ev: ev !== null && (EVIDENCE as readonly string[]).includes(ev) ? (ev as EvidenceKind) : null,
    edit: edit !== null && (EDITS as readonly string[]).includes(edit) ? (edit as EditKind) : null,
    element: el !== null && el.trim() !== '' ? el : null,
    uncertain: q.get('usikre') === '1',
  };
}

/**
 * The query string (no leading `?`) for `sel`, with defaults left out.
 * `keep` is the current query: keys the selection does not own (`pano`,
 * `captureFor`, …) are carried over as they are, in their order.
 */
export function format(sel: Selection, keep?: string | URLSearchParams): string {
  const out = new URLSearchParams();
  const owned = new Set<string>(SELECTION_KEYS);
  if (keep !== undefined) {
    const from = typeof keep === 'string' ? new URLSearchParams(keep) : keep;
    for (const [key, value] of from) if (!owned.has(key)) out.append(key, value);
  }
  const base = defaultSelection(sel.mode);
  if (sel.typeId !== null) out.set('type', String(sel.typeId));
  if (sel.partCode !== null) out.set('part', sel.partCode);
  if (sel.sampleId !== null) out.set('sample', String(sel.sampleId));
  if (sel.room !== null) out.set('room', String(sel.room));
  if (sel.storey !== null) out.set('storey', String(sel.storey));
  if (sel.lens !== base.lens) out.set('lens', sel.lens);
  if (sel.view !== base.view) out.set('view', sel.view);
  if (sel.tview !== base.tview) out.set('tview', sel.tview);
  if (sel.fieldView !== null) out.set('fields', formatFieldView(sel.fieldView));
  if (sel.search.trim() !== '') out.set('q', sel.search);
  if (sel.env !== null) out.set('env', sel.env);
  if (sel.starred) out.set('star', '1');
  if (sel.errors) out.set('errors', '1');
  if (sel.page === INDBERETNING_PAGE) out.set('page', 'indberetning');
  else if (sel.page !== base.page) out.set('page', String(sel.page));
  if (sel.rapport === 'miljoe') out.set('rapport', sel.rapport);
  if (sel.ev !== null) out.set('ev', sel.ev);
  if (sel.edit !== null) out.set('edit', sel.edit);
  if (sel.element !== null) out.set('el', sel.element);
  if (sel.uncertain) out.set('usikre', '1');
  return out.toString();
}

/**
 * `sel` with `patch` applied, keeping the selection coherent: a new type
 * drops the part and evidence that belonged to the old one (unless the patch
 * sets them too) and clears the bulk part selection.
 */
export function applyPatch(sel: Selection, patch: Partial<Selection>): Selection {
  const next: Selection = { ...sel, ...patch };
  const typeChanged = 'typeId' in patch && patch.typeId !== sel.typeId;
  if (typeChanged) {
    if (!('partCode' in patch)) next.partCode = null;
    if (!('ev' in patch)) next.ev = null;
    if (!('selParts' in patch)) next.selParts = [];
  }
  if ('partCode' in patch && patch.partCode === null && !('ev' in patch)) next.ev = null;
  return next;
}

/**
 * The patch that focuses storey `id` (null = hele bygningen), given the
 * room in focus: a room on that storey stays focused, a room elsewhere is
 * dropped. Clearing the storey keeps the room (it is in the building). The
 * other way round needs no helper: setting `room` leaves `storey` alone.
 */
export function storeyPatch(storeys: readonly Storey[], id: number | null, room: number | null): Partial<Selection> {
  if (id === null || room === null) return { storey: id };
  return storeyRooms(storeys, id).includes(room) ? { storey: id } : { storey: id, room: null };
}

/**
 * The selection after switching to `mode`: the focus (type, sample, room,
 * storey, lens, table view, field view) travels along, the mode-local state (evidence
 * tile, Sagsdata editor, picked parts, Ressourcer's filters, Rediger's
 * element and filter) does not.
 */
export function switchMode(sel: Selection, mode: ModeId): Selection {
  if (sel.mode === mode) return sel;
  return {
    ...sel,
    mode,
    ev: null,
    edit: null,
    element: null,
    uncertain: false,
    selParts: [],
    search: '',
    env: null,
    starred: false,
    errors: false,
  };
}

/**
 * The id after `current` in `ids` (J): clamps at the end. With nothing (or
 * something not in the list) selected, starts at the first id. Null for an
 * empty list.
 */
export function next<T>(ids: readonly T[], current: T | null): T | null {
  return step(ids, current, 1);
}

/** The id before `current` in `ids` (K): clamps at the start. */
export function prev<T>(ids: readonly T[], current: T | null): T | null {
  return step(ids, current, -1);
}

export function step<T>(ids: readonly T[], current: T | null, delta: number): T | null {
  if (ids.length === 0) return null;
  const at = current === null ? -1 : ids.indexOf(current);
  if (at < 0) return ids[0];
  const to = Math.min(ids.length - 1, Math.max(0, at + delta));
  return ids[to];
}

/** `selParts` with `code` toggled (shift-click, a Tabel › Dele checkbox). Order kept. */
export function togglePart(selParts: readonly string[], code: string): string[] {
  return selParts.includes(code) ? selParts.filter((c) => c !== code) : [...selParts, code];
}

/**
 * A click on a part chip in the type inspector (`PartChips`), as a patch.
 * Ressourcer: a plain click opens the part (its own inspector), keeping the
 * picks; an `additive` click (Shift/⌘/Ctrl) toggles it in the picks without
 * opening it, like Shift-click in the model. Gennemgang (`review`): every
 * click toggles the pick — picking a part focuses it, un-picking the focused
 * part lets go of the focus.
 */
export function partChipClick(
  sel: Pick<Selection, 'partCode' | 'selParts'>,
  code: string,
  additive: boolean,
  variant: 'ressourcer' | 'review' = 'ressourcer',
): Partial<Selection> {
  const selParts = togglePart(sel.selParts, code);
  if (variant === 'review') {
    const adding = !sel.selParts.includes(code);
    return { selParts, partCode: adding ? code : sel.partCode === code ? null : sel.partCode };
  }
  return additive ? { selParts } : { partCode: code, selParts: [...sel.selParts] };
}
